/**
 * NgwEditorTheme — `IEditorTheme` 의 NgwEditor 본 구현 (PR-N2).
 *
 * 사용자 결정 (a) — TinyMCE 1:1 이전 (nine_tinymce_desktop.js L5532~L5686).
 *
 * 매핑 전략 (05 §4.7):
 *   - `setDarkMode(isDark)`: 어댑터 상태 보존 + mount element 에 `ngw-dark` className 토글 +
 *     기존 fore/back color 가 보존돼 있으면 다시 적용/해제.
 *   - `setDarkBackColor(color)`: 색 lowercase + chroma luminance 계산 후 보존. 다크 모드 활성
 *     상태면 mount element 의 `style.backgroundColor` + `--dark-mode-back-color` CSS variable
 *     주입 (nine_tinymce_desktop.js L5640~L5646 1:1).
 *   - `setDarkForeColor(color)`: 색 lowercase + chroma luminance 계산. 다크 모드 활성 상태면
 *     ownerDocument.head 에 `id="ngw-darkmode-info"` style tag 주입 + `.ngw-dark` 셀렉터로 본문
 *     색 적용. parent app CSS 와 격리 — TinyMCE iframe 와 달리 inline 마운트이므로 className
 *     scope 필수.
 *   - `removeDarkForeColor()`: style tag 제거.
 *
 * **link color 보정**: TinyMCE 의 `_getDarkModeColor` 가 수행하는 색 반전은 PR-N6 의 mail
 * region styling 작업과 함께 정밀화 예정 — 본 PR 단계는 body color 만 적용.
 */

import chroma from 'chroma-js'
import type { IEditorTheme } from '../IEditorTheme'
import type { NgwEditorAdapter } from './NgwEditorAdapter'

const DARK_MODE_CLASS = 'ngw-dark'
const DARK_STYLE_TAG_ID = 'ngw-darkmode-info'

export class NgwEditorTheme implements IEditorTheme {
  private readonly adapter: NgwEditorAdapter
  private darkMode = false
  private backColor = ''
  private foreColor = ''
  /** 보존 — TinyMCE 의 darkModeInfo.backLum 와 동등. 외부 노출은 없으나 색 변환 호환성 보장용. */
  private backLum: number | null = null
  private foreLum: number | null = null

  constructor(adapter: NgwEditorAdapter) {
    this.adapter = adapter
  }

  // TODO(PR-N9e 후보): TinyMCE 는 addSendAvailability / setQuotedHeader / setQuotedBody 등 본문
  // 삽입 직후 isDarkMode 분기로 _checkElemColor + _checkStyleTagCss 후처리를 호출한다 (예:
  // nine_tinymce_desktop.js L7354-L7357). NgwEditor 의 다크모드 처리는 글로벌 CSS / contentCSS
  // 의존이라 시간표 atom 노드의 inline style (#0078D7 / #E9F3FB / #106EBE 등) 이 다크 배경과
  // 부조화 가능. 사용자 결정 (2026-05-19): 다크모드 자체가 현재 사용 안 됨 — 기능 활성화 시
  // 별도 PR 로 진입 (시간표 외 다른 inline style 색상도 동일 보강 대상).
  setDarkMode(isDark: boolean): void {
    if (this.darkMode === isDark) {
      return
    }
    this.darkMode = isDark
    const dom = this.getMountElement()
    if (dom == null) {
      return
    }
    if (isDark) {
      dom.classList.add(DARK_MODE_CLASS)
      if (this.backColor.length > 0) {
        this.applyBackColorToDOM(dom)
      }
      if (this.foreColor.length > 0) {
        this.applyForeStyleTag(dom)
      }
    } else {
      dom.classList.remove(DARK_MODE_CLASS)
      dom.style.backgroundColor = ''
      dom.style.removeProperty('--dark-mode-back-color')
      this.removeForeStyleTag(dom)
    }
  }

  isDarkMode(): boolean {
    return this.darkMode
  }

  getDarkBackColor(): string {
    return this.backColor
  }

  setDarkBackColor(color: string): void {
    this.backColor = color.toLowerCase()
    this.backLum = safeLuminance(this.backColor)
    if (!this.darkMode) {
      return
    }
    const dom = this.getMountElement()
    if (dom != null) {
      this.applyBackColorToDOM(dom)
    }
  }

  getDarkForeColor(): string {
    return this.foreColor
  }

  setDarkForeColor(color: string): void {
    this.foreColor = color.toLowerCase()
    this.foreLum = safeLuminance(this.foreColor)
    if (!this.darkMode) {
      return
    }
    const dom = this.getMountElement()
    if (dom != null) {
      this.applyForeStyleTag(dom)
    }
  }

  removeDarkForeColor(): void {
    this.foreColor = ''
    this.foreLum = null
    const dom = this.getMountElement()
    if (dom != null) {
      this.removeForeStyleTag(dom)
    }
  }

  /** Test 용 luminance 노출 — chroma 계산이 1:1 이전됐는지 검증. @internal */
  getBackLuminance(): number | null {
    return this.backLum
  }

  /** Test 용 luminance 노출. @internal */
  getForeLuminance(): number | null {
    return this.foreLum
  }

  private getMountElement(): HTMLElement | null {
    return this.adapter.lifecycle.getHandle()?.getEditorDOM() ?? null
  }

  private applyBackColorToDOM(dom: HTMLElement): void {
    dom.style.backgroundColor = this.backColor
    dom.style.setProperty('--dark-mode-back-color', this.backColor)
  }

  private applyForeStyleTag(dom: HTMLElement): void {
    const doc = dom.ownerDocument
    let styleTag = doc.getElementById(DARK_STYLE_TAG_ID) as HTMLStyleElement | null
    if (styleTag == null) {
      styleTag = doc.createElement('style')
      styleTag.id = DARK_STYLE_TAG_ID
      doc.head.appendChild(styleTag)
    }
    styleTag.textContent = `.${DARK_MODE_CLASS} { color: ${this.foreColor}; }\n`
  }

  private removeForeStyleTag(dom: HTMLElement): void {
    const doc = dom.ownerDocument
    const styleTag = doc.getElementById(DARK_STYLE_TAG_ID)
    if (styleTag != null) {
      styleTag.parentElement?.removeChild(styleTag)
    }
  }
}

function safeLuminance(color: string): number | null {
  if (color.length === 0) {
    return null
  }
  try {
    return chroma(color).luminance()
  } catch {
    return null
  }
}
